import { useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';

import type {
  AdminApi,
  ChecklistTemplateAdmin,
  ConfigurationImpactAssessment,
  PublishedTemplateVersionResult,
  TemplateAuthoringDraftSummary,
  TemplateImportProfile,
  TemplateImportRun,
  TemplateSourceArtifact,
} from './admin-api';
import { ApiError } from './api';
import { problemMessage, problemTechnicalDetail } from './admin-presentation';
import { generateInternalCode } from './admin-internal-codes';
import {
  ASSESSMENTS,
  defaultMappingForFamily,
  buildMappingPayload,
  mappingFromUnknown,
  MAPPING_SCHEMA,
  validateMapping,
  type DeclarativeMapping,
} from './template-admin-mapping';
import {
  activeVersionLabel,
  canPublishImportRun,
  importRunStatusLabel,
  normalizeLogicalFilename,
  TEMPLATE_APPLY_IMPACT_INTRO,
  TEMPLATE_APPLY_SUCCESS_NOTICE,
  templateActivationImpactRows,
  templateBusinessLabel,
  templatePrimaryDisplayName,
  validationIssueExplanation,
  issueContext,
} from './template-admin-presentation';
import { DraftEditor } from './template-authoring-editor';
import {
  authoringDraftStatusHint,
  authoringProvenanceLabel,
  canDiscardAuthoringDraft,
  constructorChecklistRowMeta,
  draftVersionRowTitle,
  formatAuthoringTimestamp,
  isOperationalAuthoringDraftStatus,
  publishedVersionUserStatus,
  templateVersionBusinessLabel,
} from './template-authoring-presentation';
import { proposeNextVersionCode } from './template-authoring-version-code';

type BuilderView =
  | { readonly kind: 'list' }
  | { readonly kind: 'checklist'; readonly checklistId: string }
  | { readonly kind: 'create'; readonly mode: 'choose' | 'manual' | 'excel' | 'clone' }
  | {
      readonly kind: 'editor';
      readonly draftId: string;
      readonly returnChecklistId: string | null;
    };

type CreatedDraftTarget = {
  readonly draftId: string;
  readonly checklistTemplateId: string;
};

type TemplateVersionRow = Awaited<ReturnType<AdminApi['listChecklistTemplateVersions']>>[number];

type RuntimePreviewTarget = {
  readonly templateId: string;
  readonly templateDisplayName: string;
  readonly versionId: string;
  readonly versionCode: string;
  readonly isActive: boolean;
};

function ProblemAlert(props: { readonly failure: unknown }): ReactElement {
  const technical = problemTechnicalDetail(props.failure);
  return (
    <div role="alert" className="banner banner-closed">
      <p>{problemMessage(props.failure)}</p>
      {technical !== null ? <p className="meta">Технические сведения: {technical}</p> : null}
    </div>
  );
}

export function TemplateAuthoringAdmin(props: {
  readonly api: AdminApi;
  readonly templates: ChecklistTemplateAdmin[] | null;
  readonly initialDraftId?: string | null;
  readonly onInitialDraftConsumed?: () => void;
  readonly onPublished?: (result: PublishedTemplateVersionResult) => void;
  readonly onCatalogChanged?: () => void;
  readonly onOpenRuntimePreview?: (target: RuntimePreviewTarget) => void;
}): ReactElement {
  const [view, setView] = useState<BuilderView>(
    props.initialDraftId !== null && props.initialDraftId !== undefined
      ? { kind: 'editor', draftId: props.initialDraftId, returnChecklistId: null }
      : { kind: 'list' },
  );
  const [listSearch, setListSearch] = useState('');
  const [drafts, setDrafts] = useState<TemplateAuthoringDraftSummary[] | null>(null);
  const [draftsError, setDraftsError] = useState<unknown>(null);
  const [draftsLoading, setDraftsLoading] = useState(true);
  const [versionsByChecklistId, setVersionsByChecklistId] = useState<
    Record<string, readonly TemplateVersionRow[]>
  >({});
  const draftsLoadSeq = useRef(0);
  const draftsRef = useRef<TemplateAuthoringDraftSummary[] | null>(null);
  draftsRef.current = drafts;

  const reloadDrafts = () => {
    const seq = ++draftsLoadSeq.current;
    const keepPrevious = draftsRef.current !== null;
    if (!keepPrevious) {
      setDraftsLoading(true);
    }
    setDraftsError(null);
    void props.api
      .listTemplateAuthoringDrafts()
      .then((items) => {
        if (seq !== draftsLoadSeq.current) {
          return;
        }
        setDrafts(items.filter((row) => isOperationalAuthoringDraftStatus(row.status)));
        setDraftsLoading(false);
      })
      .catch((failure: unknown) => {
        if (seq !== draftsLoadSeq.current) {
          return;
        }
        setDraftsError(failure);
        if (!keepPrevious) {
          setDrafts(null);
        }
        setDraftsLoading(false);
      });
  };

  useEffect(() => {
    reloadDrafts();
  }, [props.api]);

  useEffect(() => {
    if (props.initialDraftId !== null && props.initialDraftId !== undefined) {
      setView({
        kind: 'editor',
        draftId: props.initialDraftId,
        returnChecklistId: null,
      });
      props.onInitialDraftConsumed?.();
    }
  }, [props.initialDraftId]);

  const cacheVersions = (checklistId: string, rows: readonly TemplateVersionRow[]) => {
    setVersionsByChecklistId((current) => ({ ...current, [checklistId]: rows }));
  };

  const invalidateVersions = (checklistId: string) => {
    setVersionsByChecklistId((current) => {
      if (current[checklistId] === undefined) {
        return current;
      }
      const next = { ...current };
      delete next[checklistId];
      return next;
    });
  };

  const openChecklist = (checklistId: string) => {
    setView({ kind: 'checklist', checklistId });
  };

  const openEditor = (draftId: string, returnChecklistId: string | null) => {
    setView({ kind: 'editor', draftId, returnChecklistId });
  };

  const handleCreated = (target: CreatedDraftTarget) => {
    props.onCatalogChanged?.();
    reloadDrafts();
    setView({ kind: 'checklist', checklistId: target.checklistTemplateId });
  };

  if (view.kind === 'editor') {
    const returnChecklistId = view.returnChecklistId;
    return (
      <DraftEditor
        api={props.api}
        draftId={view.draftId}
        templates={props.templates}
        onBack={() => {
          reloadDrafts();
          if (returnChecklistId !== null) {
            setView({ kind: 'checklist', checklistId: returnChecklistId });
            return;
          }
          setView({ kind: 'list' });
        }}
        onPublished={(result) => {
          reloadDrafts();
          invalidateVersions(result.template_id);
          setView({ kind: 'checklist', checklistId: result.template_id });
          props.onPublished?.(result);
        }}
      />
    );
  }

  if (view.kind === 'create') {
    return (
      <CreateDraftFlow
        api={props.api}
        templates={props.templates}
        mode={view.mode}
        onModeChange={(mode) => setView({ kind: 'create', mode })}
        onCancel={() => setView({ kind: 'list' })}
        onCreated={handleCreated}
        onCatalogChanged={props.onCatalogChanged}
      />
    );
  }

  if (view.kind === 'checklist') {
    const checklist =
      props.templates?.find((row) => row.id === view.checklistId) ?? null;
    return (
      <ConstructorChecklistDetail
        api={props.api}
        checklistId={view.checklistId}
        checklist={checklist}
        drafts={drafts}
        draftsLoading={draftsLoading}
        versions={versionsByChecklistId[view.checklistId] ?? null}
        onVersionsLoaded={(rows) => cacheVersions(view.checklistId, rows)}
        onBack={() => setView({ kind: 'list' })}
        onOpenEditor={(draftId) => openEditor(draftId, view.checklistId)}
        onDraftsChanged={reloadDrafts}
        onCatalogChanged={props.onCatalogChanged}
        onInvalidateVersions={() => invalidateVersions(view.checklistId)}
        onOpenRuntimePreview={props.onOpenRuntimePreview}
      />
    );
  }

  return (
    <ConstructorChecklistList
      api={props.api}
      templates={props.templates}
      drafts={drafts}
      draftsLoading={draftsLoading}
      draftsError={draftsError}
      search={listSearch}
      onSearchChange={setListSearch}
      onCreate={() => setView({ kind: 'create', mode: 'choose' })}
      onOpenChecklist={openChecklist}
      onRetryDrafts={reloadDrafts}
      onCatalogChanged={props.onCatalogChanged}
    />
  );
}

function ConstructorChecklistList(props: {
  readonly api: AdminApi;
  readonly templates: ChecklistTemplateAdmin[] | null;
  readonly drafts: TemplateAuthoringDraftSummary[] | null;
  readonly draftsLoading: boolean;
  readonly draftsError: unknown;
  readonly search: string;
  readonly onSearchChange: (value: string) => void;
  readonly onCreate: () => void;
  readonly onOpenChecklist: (checklistId: string) => void;
  readonly onRetryDrafts: () => void;
  readonly onCatalogChanged?: () => void;
}): ReactElement {
  const [confirmSoftDelete, setConfirmSoftDelete] = useState<ChecklistTemplateAdmin | null>(null);
  const [softDeleteBusy, setSoftDeleteBusy] = useState(false);
  const [softDeleteError, setSoftDeleteError] = useState<unknown>(null);
  const [softDeleteSuccess, setSoftDeleteSuccess] = useState<string | null>(null);
  const draftIdsByChecklist = new Set(
    (props.drafts ?? []).map((row) => row.checklist_template_id),
  );
  const query = props.search.trim().toLocaleLowerCase('ru-RU');
  const rows =
    props.templates === null
      ? null
      : props.templates.filter((template) => {
          if (query.length === 0) {
            return true;
          }
          const name = templatePrimaryDisplayName(template.name).toLocaleLowerCase('ru-RU');
          const stored = template.name.toLocaleLowerCase('ru-RU');
          return name.includes(query) || stored.includes(query);
        });

  return (
    <>
      <h2>Конструктор</h2>
      <p className="meta">
        Список чек-листов. Версии (черновик / активная / архивная) открываются внутри выбранного
        чек-листа.
      </p>
      <div className="actions">
        <button type="button" onClick={props.onCreate}>
          Создать чек-лист
        </button>
      </div>
      {props.draftsError !== null ? (
        <>
          <ProblemAlert failure={props.draftsError} />
          <button type="button" onClick={props.onRetryDrafts} disabled={props.draftsLoading}>
            Повторить
          </button>
        </>
      ) : null}
      {softDeleteError !== null ? <ProblemAlert failure={softDeleteError} /> : null}
      {softDeleteSuccess !== null ? (
        <p role="status" className="banner">
          {softDeleteSuccess}
        </p>
      ) : null}
      {confirmSoftDelete !== null ? (
        <div className="info-card stack-gap" role="alertdialog" aria-labelledby="ctor-soft-delete-title">
          <h3 id="ctor-soft-delete-title" className="section-title">
            Переместить в «Удалённые»?
          </h3>
          <p>
            Чек-лист «{templatePrimaryDisplayName(confirmSoftDelete.name)}» (
            {confirmSoftDelete.template_code}) будет скрыт из конструктора. Версии и черновики
            сохраняются. Восстановление выполняется в «Чек-листы → Удалённые» и не активирует
            версию.
          </p>
          <div className="actions">
            <button
              type="button"
              className="authoring-field-icon-btn"
              title="Подтвердить перемещение в удалённые"
              aria-label="Подтвердить перемещение в удалённые"
              disabled={softDeleteBusy}
              onClick={() => {
                const target = confirmSoftDelete;
                setSoftDeleteBusy(true);
                setSoftDeleteError(null);
                setSoftDeleteSuccess(null);
                void props.api
                  .softDeleteChecklistTemplate(target.id, {
                    expected_version: target.version,
                  })
                  .then(() => {
                    setConfirmSoftDelete(null);
                    setSoftDeleteSuccess(
                      `Чек-лист ${target.template_code} перемещён в «Удалённые».`,
                    );
                    props.onCatalogChanged?.();
                  })
                  .catch((failure: unknown) => {
                    setSoftDeleteError(failure);
                  })
                  .finally(() => setSoftDeleteBusy(false));
              }}
            >
              <span aria-hidden="true">✓</span>
            </button>
            <button
              type="button"
              className="authoring-field-icon-btn"
              title="Отменить"
              aria-label="Отменить"
              disabled={softDeleteBusy}
              onClick={() => setConfirmSoftDelete(null)}
            >
              <span aria-hidden="true">✕</span>
            </button>
          </div>
        </div>
      ) : null}
      {props.templates === null || (props.draftsLoading && props.drafts === null) ? (
        <p role="status">Загрузка…</p>
      ) : null}
      {props.templates !== null && props.templates.length === 0 ? (
        <p role="status" className="banner">
          Чек-листы ещё не созданы. Нажмите «Создать чек-лист».
        </p>
      ) : null}
      {props.templates !== null && props.templates.length > 0 ? (
        <>
          <label htmlFor="tad_checklist_search">
            Поиск
            <input
              id="tad_checklist_search"
              type="search"
              value={props.search}
              placeholder="Поиск по названию чек-листа"
              onChange={(event) => props.onSearchChange(event.target.value)}
            />
          </label>
          {rows !== null && rows.length === 0 ? (
            <p role="status" className="banner">
              Нет чек-листов по текущему поиску.
            </p>
          ) : null}
          {rows !== null && rows.length > 0 ? (
            <ul className="admin-compact-list" aria-label="Чек-листы конструктора">
              {rows.map((template) => {
                const title = templatePrimaryDisplayName(template.name);
                const meta = constructorChecklistRowMeta(
                  template,
                  draftIdsByChecklist.has(template.id),
                );
                return (
                  <li key={template.id} className="admin-compact-row">
                    <div className="admin-compact-row-main">
                      <button
                        type="button"
                        className="admin-template-list-btn"
                        onClick={() => props.onOpenChecklist(template.id)}
                      >
                        <span className="admin-compact-row-text">
                          <span className="admin-compact-row-title" title={title}>
                            {title}
                          </span>
                          <span className="admin-compact-row-meta" title={meta}>
                            {meta}
                          </span>
                        </span>
                        <span className="admin-compact-chevron" aria-hidden="true">
                          ›
                        </span>
                      </button>
                      <div className="admin-compact-row-actions">
                        <button
                          type="button"
                          className="authoring-field-icon-btn"
                          title="Переместить в удалённые"
                          aria-label="Переместить в удалённые"
                          disabled={softDeleteBusy}
                          onClick={() => {
                            setConfirmSoftDelete(template);
                            setSoftDeleteError(null);
                            setSoftDeleteSuccess(null);
                          }}
                        >
                          <span aria-hidden="true">🗑</span>
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </>
      ) : null}
    </>
  );
}

function ConstructorChecklistDetail(props: {
  readonly api: AdminApi;
  readonly checklistId: string;
  readonly checklist: ChecklistTemplateAdmin | null;
  readonly drafts: TemplateAuthoringDraftSummary[] | null;
  readonly draftsLoading: boolean;
  readonly versions: readonly TemplateVersionRow[] | null;
  readonly onVersionsLoaded: (rows: readonly TemplateVersionRow[]) => void;
  readonly onBack: () => void;
  readonly onOpenEditor: (draftId: string) => void;
  readonly onDraftsChanged: () => void;
  readonly onCatalogChanged?: () => void;
  readonly onInvalidateVersions: () => void;
  readonly onOpenRuntimePreview?: (target: RuntimePreviewTarget) => void;
}): ReactElement {
  const [versionsLoading, setVersionsLoading] = useState(props.versions === null);
  const [versionsError, setVersionsError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmDiscardId, setConfirmDiscardId] = useState<string | null>(null);
  const [techOpenId, setTechOpenId] = useState<string | null>(null);
  const [applyPhase, setApplyPhase] = useState<
    | 'idle'
    | 'validating'
    | 'publishing'
    | 'assessing'
    | 'confirm'
    | 'activating'
    | 'needs-reassess'
  >('idle');
  const [assessment, setAssessment] = useState<ConfigurationImpactAssessment | null>(null);
  const [applyHint, setApplyHint] = useState<string | null>(null);
  const [applyVersionId, setApplyVersionId] = useState<string | null>(null);
  const [applyDraftId, setApplyDraftId] = useState<string | null>(null);
  const [proposedCodes, setProposedCodes] = useState<Record<string, string>>({});
  const loadSeq = useRef(0);
  const listRootRef = useRef<HTMLUListElement | null>(null);

  const checklistDrafts =
    props.drafts === null
      ? null
      : [...props.drafts]
          .filter((row) => row.checklist_template_id === props.checklistId)
          .sort((a, b) => b.updated_at.localeCompare(a.updated_at));

  const versions = props.versions;

  useEffect(() => {
    if (props.versions !== null) {
      setVersionsLoading(false);
      setVersionsError(null);
      return;
    }
    const seq = ++loadSeq.current;
    setVersionsLoading(true);
    setVersionsError(null);
    void props.api
      .listChecklistTemplateVersions(props.checklistId)
      .then((rows) => {
        if (seq !== loadSeq.current) {
          return;
        }
        props.onVersionsLoaded(rows);
        setVersionsLoading(false);
      })
      .catch((failure: unknown) => {
        if (seq !== loadSeq.current) {
          return;
        }
        setVersionsError(failure);
        setVersionsLoading(false);
      });
  }, [props.api, props.checklistId, props.versions]);

  const draftIdsKey = (checklistDrafts ?? []).map((row) => row.id).join(',');
  const versionCodesKey = (versions ?? []).map((row) => row.version_code).join(',');
  useEffect(() => {
    if (checklistDrafts === null || versions === null) {
      return;
    }
    const nextCode = proposeNextVersionCode(versions.map((row) => row.version_code));
    setProposedCodes((current) => {
      let changed = false;
      const next: Record<string, string> = { ...current };
      for (const draft of checklistDrafts) {
        if (next[draft.id] === undefined) {
          next[draft.id] = nextCode;
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [draftIdsKey, versionCodesKey]);

  useEffect(() => {
    if (techOpenId === null) {
      return;
    }
    const onPointerDown = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      const root = listRootRef.current;
      if (root !== null && root.contains(target)) {
        const host = (target as HTMLElement).closest?.('[data-draft-tech-host]');
        if (host !== null && host !== undefined) {
          return;
        }
      }
      setTechOpenId(null);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setTechOpenId(null);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [techOpenId]);

  const resetApplySurface = () => {
    setApplyPhase('idle');
    setAssessment(null);
    setApplyHint(null);
    setApplyVersionId(null);
    setApplyDraftId(null);
  };

  const refreshVersions = () => {
    props.onInvalidateVersions();
    return props.api.listChecklistTemplateVersions(props.checklistId).then((rows) => {
      props.onVersionsLoaded(rows);
      return rows;
    });
  };

  const confirmRow =
    confirmDiscardId === null || checklistDrafts === null
      ? null
      : (checklistDrafts.find((row) => row.id === confirmDiscardId) ?? null);

  const title =
    props.checklist !== null
      ? templatePrimaryDisplayName(props.checklist.name)
      : 'Чек-лист';

  const archivedVersions =
    versions === null
      ? []
      : [...versions]
          .filter((row) => row.is_active !== true)
          .sort((a, b) => b.approved_at.localeCompare(a.approved_at));
  const activeVersions =
    versions === null ? [] : versions.filter((row) => row.is_active === true);

  const createManualDraft = () => {
    if (props.checklist === null || busy) {
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    setNotice(null);
    void props.api
      .createTemplateAuthoringDraft(props.checklist.id, props.checklist.version)
      .then((draft) => {
        setBusy(false);
        props.onDraftsChanged();
        props.onCatalogChanged?.();
        setSuccess('Первая версия создана как черновик.');
        props.onOpenEditor(draft.id);
      })
      .catch((failure: unknown) => {
        setBusy(false);
        setError(failure);
      });
  };

  const createDraftFromVersion = (version: TemplateVersionRow) => {
    if (props.checklist === null || busy) {
      return;
    }
    if (checklistDrafts !== null && checklistDrafts.length > 0) {
      setNotice(
        'У этого чек-листа уже есть рабочий черновик. Будет создан дополнительный черновик.',
      );
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    void props.api
      .createTemplateAuthoringDraftFromTemplateVersion(
        version.id,
        props.checklist.id,
        props.checklist.version,
      )
      .then((draft) => {
        setBusy(false);
        props.onDraftsChanged();
        setSuccess('Новая версия создана как черновик.');
        props.onOpenEditor(draft.id);
      })
      .catch((failure: unknown) => {
        setBusy(false);
        setError(failure);
      });
  };

  const createNewVersion = () => {
    if (versions === null || versions.length === 0) {
      createManualDraft();
      return;
    }
    const source = versions.find((row) => row.is_active === true) ?? versions[0] ?? null;
    if (source === null) {
      return;
    }
    createDraftFromVersion(source);
  };

  const assessAndConfirm = (templateVersionId: string, expectedVersion: string) => {
    setApplyPhase('assessing');
    setAssessment(null);
    setApplyVersionId(templateVersionId);
    void props.api
      .assessChecklistTemplateActivationImpact(props.checklistId, {
        expected_version: expectedVersion,
        template_version_id: templateVersionId,
        impact_mode: 'MANDATORY_PRESTART',
      })
      .then((row) => {
        setAssessment(row);
        setApplyPhase('confirm');
        setBusy(false);
      })
      .catch((failure: unknown) => {
        setBusy(false);
        setApplyPhase('idle');
        setNotice('Версия сохранена, но применить её не удалось.');
        setError(failure);
        void refreshVersions();
        props.onDraftsChanged();
      });
  };

  const applyDraftChanges = (draft: TemplateAuthoringDraftSummary) => {
    if (props.checklist === null || busy) {
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    setNotice(null);
    setApplyDraftId(draft.id);
    setApplyPhase('validating');
    const versionCode =
      (proposedCodes[draft.id] ?? '').trim().length > 0
        ? proposedCodes[draft.id]!.trim()
        : proposeNextVersionCode((versions ?? []).map((row) => row.version_code));
    let publishedReached = false;

    void props.api
      .getTemplateAuthoringDraft(draft.id)
      .then((fresh) =>
        props.api.validateTemplateAuthoringDraft(fresh.id, fresh.version).then((report) => {
          if (report.errors.length > 0) {
            throw Object.assign(new Error('Проверка черновика не пройдена. Исправьте ошибки.'), {
              validationReport: report,
            });
          }
          setApplyPhase('publishing');
          return props.api
            .getTemplateAuthoringDraft(fresh.id)
            .then((afterValidate) =>
              props.api.publishTemplateAuthoringDraft(
                afterValidate.id,
                afterValidate.version,
                versionCode,
              ),
            );
        }),
      )
      .then((published) => {
        publishedReached = true;
        props.onDraftsChanged();
        props.onCatalogChanged?.();
        return refreshVersions().then(() =>
          props.api.listChecklistTemplates().then((rows) => {
            const freshTemplate = rows.find((row) => row.id === props.checklistId);
            if (freshTemplate === undefined) {
              throw new Error('Чек-лист отсутствует в загруженном каталоге.');
            }
            assessAndConfirm(published.id, freshTemplate.version);
          }),
        );
      })
      .catch((failure: unknown) => {
        setBusy(false);
        if (publishedReached) {
          setNotice('Версия сохранена, но применить её не удалось.');
          void refreshVersions();
          props.onDraftsChanged();
        }
        setApplyPhase('idle');
        setApplyDraftId(null);
        setError(failure);
      });
  };

  const startArchiveApply = (version: TemplateVersionRow) => {
    if (props.checklist === null || busy) {
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    setNotice(null);
    setApplyDraftId(null);
    assessAndConfirm(version.id, props.checklist.version);
  };

  const confirmActivate = () => {
    if (props.checklist === null || assessment === null || applyVersionId === null) {
      return;
    }
    setApplyPhase('activating');
    setError(null);
    void props.api
      .activateChecklistTemplateVersion(props.checklist.id, {
        expected_version: assessment.expected_root_version,
        template_version_id: applyVersionId,
        impact_assessment_id: assessment.id,
        impact_mode: 'MANDATORY_PRESTART',
      })
      .then(() => {
        resetApplySurface();
        setBusy(false);
        setSuccess(TEMPLATE_APPLY_SUCCESS_NOTICE);
        props.onCatalogChanged?.();
        props.onDraftsChanged();
        void refreshVersions();
      })
      .catch((failure: unknown) => {
        const code = failure instanceof ApiError ? failure.problem.code : undefined;
        if (code === 'STALE_VERSION' || code === 'INVALID_ACTIVATION_TARGET') {
          setAssessment(null);
          setApplyPhase('needs-reassess');
          setApplyHint(
            code === 'STALE_VERSION'
              ? 'Чек-лист изменился в другой сессии. Данные обновлены — повторите применение версии.'
              : 'Данные изменились. Оцените влияние ещё раз.',
          );
          props.onCatalogChanged?.();
          setBusy(false);
          return;
        }
        setApplyPhase('confirm');
        setBusy(false);
        setNotice('Версия сохранена, но применить её не удалось.');
        setError(failure);
      });
  };

  const empty =
    !versionsLoading &&
    versions !== null &&
    versions.length === 0 &&
    checklistDrafts !== null &&
    checklistDrafts.length === 0;

  const applyBusy =
    applyPhase === 'validating' ||
    applyPhase === 'publishing' ||
    applyPhase === 'assessing' ||
    applyPhase === 'activating';

  return (
    <>
      <button type="button" className="admin-back-link" onClick={props.onBack}>
        ← К списку
      </button>
      <h2>{title}</h2>
      {props.checklist !== null ? (
        <p>
          {activeVersionLabel(
            props.checklist.active_template_version_id === null
              ? null
              : (versions?.find((row) => row.id === props.checklist!.active_template_version_id)
                  ?.version_code ??
                  props.checklist.active_version_code),
          )}
        </p>
      ) : (
        <p className="meta" role="status">
          Чек-лист отсутствует в загруженном каталоге. Обновите каталог или вернитесь к списку.
        </p>
      )}
      <p className="meta admin-checklist-hierarchy-help">
        Здесь собраны версии этого чек-листа: черновики, активная и архивные.
      </p>
      {error !== null ? <ProblemAlert failure={error} /> : null}
      {success !== null ? (
        <p role="status" className="banner">
          {success}
        </p>
      ) : null}
      {notice !== null ? (
        <p role="status" className="banner">
          {notice}
        </p>
      ) : null}

      <section className="admin-constructor-section" aria-labelledby="constructor-versions-title">
        <h3 id="constructor-versions-title" className="section-title">
          Версии чек-листа
        </h3>
        {(versionsLoading || (props.draftsLoading && checklistDrafts === null)) && (
          <p role="status">Загрузка версий…</p>
        )}
        {versionsError !== null ? <ProblemAlert failure={versionsError} /> : null}
        {empty ? (
          <>
            <p role="status" className="banner">
              Версий пока нет.
            </p>
            <div className="actions">
              <button
                type="button"
                className="authoring-field-icon-btn"
                disabled={busy || props.checklist === null}
                title="Создать первую версию"
                aria-label="Создать первую версию"
                onClick={createManualDraft}
              >
                <span aria-hidden="true">＋</span>
              </button>
            </div>
          </>
        ) : null}

        {!empty ? (
          <ul
            ref={listRootRef}
            className="admin-compact-list"
            aria-label="Версии чек-листа"
          >
            {(checklistDrafts ?? []).map((row) => {
              const statusHint = authoringDraftStatusHint(row.status);
              const provenanceLabel = authoringProvenanceLabel(row.provenance);
              const updatedLabel = formatAuthoringTimestamp(row.updated_at);
              const techOpen = techOpenId === row.id;
              const rowTitle = draftVersionRowTitle(proposedCodes[row.id] ?? null);
              return (
                <li key={`draft:${row.id}`} className="admin-compact-row admin-version-row">
                  <div className="admin-compact-row-main">
                    <div className="admin-compact-row-text">
                      <span className="admin-compact-row-title">{rowTitle}</span>
                      <span className="admin-compact-row-meta">
                        {provenanceLabel}
                        {row.status === 'VALIDATED' ? (
                          <>
                            {' · '}
                            <span title={statusHint ?? undefined}>Проверка пройдена</span>
                          </>
                        ) : null}
                        {' · '}
                        {updatedLabel}
                      </span>
                    </div>
                    <div className="admin-compact-row-actions">
                      <button
                        type="button"
                        className="authoring-field-icon-btn"
                        disabled={busy || applyBusy}
                        title="Продолжить редактирование черновика"
                        aria-label="Продолжить редактирование черновика"
                        onClick={() => {
                          setTechOpenId(null);
                          props.onOpenEditor(row.id);
                        }}
                      >
                        <span aria-hidden="true">✎</span>
                      </button>
                      <button
                        type="button"
                        className="authoring-field-icon-btn"
                        disabled={busy || applyBusy || props.checklist === null}
                        title="Применить изменения"
                        aria-label="Применить изменения"
                        onClick={() => applyDraftChanges(row)}
                      >
                        <span aria-hidden="true">✓</span>
                      </button>
                      <div className="admin-compact-tech-host" data-draft-tech-host={row.id}>
                        <button
                          type="button"
                          className="authoring-field-icon-btn"
                          title="Технические сведения"
                          aria-label="Технические сведения"
                          aria-expanded={techOpen}
                          aria-haspopup="dialog"
                          onClick={() =>
                            setTechOpenId((current) => (current === row.id ? null : row.id))
                          }
                        >
                          <span aria-hidden="true">ℹ</span>
                        </button>
                        {techOpen ? (
                          <div
                            className="admin-compact-tech-popover"
                            role="dialog"
                            aria-label="Технические сведения"
                          >
                            <p>Код чек-листа: {row.template_code}</p>
                            <p>Идентификатор черновика: {row.id}</p>
                            <p>Идентификатор чек-листа: {row.checklist_template_id}</p>
                            <p>Происхождение: {provenanceLabel}</p>
                          </div>
                        ) : null}
                      </div>
                      {canDiscardAuthoringDraft(row.status) ? (
                        <button
                          type="button"
                          className="authoring-field-icon-btn"
                          title="Удалить черновик"
                          aria-label="Удалить черновик"
                          disabled={busy || applyBusy}
                          onClick={() => {
                            setTechOpenId(null);
                            setConfirmDiscardId(row.id);
                          }}
                        >
                          <span aria-hidden="true">🗑</span>
                        </button>
                      ) : null}
                    </div>
                  </div>
                  {applyDraftId === row.id && applyPhase === 'validating' ? (
                    <p className="meta" role="status">
                      Проверяем черновик…
                    </p>
                  ) : null}
                  {applyDraftId === row.id && applyPhase === 'publishing' ? (
                    <p className="meta" role="status">
                      Сохраняем версию…
                    </p>
                  ) : null}
                </li>
              );
            })}

            {activeVersions.map((row) => (
              <li key={`version:${row.id}`} className="admin-compact-row admin-version-row">
                <div className="admin-compact-row-main">
                  <div className="admin-compact-row-text">
                    <span className="admin-compact-row-title">
                      Версия {row.version_code} · {publishedVersionUserStatus(true)}
                    </span>
                  </div>
                  <div className="admin-compact-row-actions">
                    <button
                      type="button"
                      className="authoring-field-icon-btn"
                      disabled={busy || applyBusy || props.checklist === null}
                      title="Посмотреть"
                      aria-label="Посмотреть"
                      onClick={() => {
                        if (props.checklist === null) {
                          return;
                        }
                        props.onOpenRuntimePreview?.({
                          templateId: props.checklist.id,
                          templateDisplayName: props.checklist.name,
                          versionId: row.id,
                          versionCode: row.version_code,
                          isActive: true,
                        });
                      }}
                    >
                      <span aria-hidden="true">👁</span>
                    </button>
                    <button
                      type="button"
                      className="authoring-field-icon-btn"
                      disabled={busy || applyBusy || props.checklist === null}
                      title="Создать новую версию на основе этой"
                      aria-label="Создать новую версию на основе этой"
                      onClick={() => createDraftFromVersion(row)}
                    >
                      <span aria-hidden="true">⎘</span>
                    </button>
                  </div>
                </div>
              </li>
            ))}

            {archivedVersions.map((row) => (
              <li key={`version:${row.id}`} className="admin-compact-row admin-version-row">
                <div className="admin-compact-row-main">
                  <div className="admin-compact-row-text">
                    <span
                      className="admin-compact-row-title"
                      title="Не является текущей версией. Сохранена для истории и ранее созданных осмотров."
                    >
                      Версия {row.version_code} · {publishedVersionUserStatus(false)}
                    </span>
                    <span className="admin-compact-row-meta">
                      Не является текущей версией. Сохранена для истории и ранее созданных осмотров.
                    </span>
                  </div>
                  <div className="admin-compact-row-actions">
                    <button
                      type="button"
                      className="authoring-field-icon-btn"
                      disabled={busy || applyBusy || props.checklist === null}
                      title="Посмотреть"
                      aria-label="Посмотреть"
                      onClick={() => {
                        if (props.checklist === null) {
                          return;
                        }
                        props.onOpenRuntimePreview?.({
                          templateId: props.checklist.id,
                          templateDisplayName: props.checklist.name,
                          versionId: row.id,
                          versionCode: row.version_code,
                          isActive: false,
                        });
                      }}
                    >
                      <span aria-hidden="true">👁</span>
                    </button>
                    <button
                      type="button"
                      className="authoring-field-icon-btn"
                      disabled={busy || applyBusy || props.checklist === null}
                      title="Создать новую версию на основе этой"
                      aria-label="Создать новую версию на основе этой"
                      onClick={() => createDraftFromVersion(row)}
                    >
                      <span aria-hidden="true">⎘</span>
                    </button>
                    <button
                      type="button"
                      className="authoring-field-icon-btn"
                      disabled={busy || applyBusy || props.checklist === null}
                      title="Сделать активной"
                      aria-label="Сделать активной"
                      onClick={() => startArchiveApply(row)}
                    >
                      <span aria-hidden="true">✓</span>
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : null}

        {versions !== null && versions.length > 0 ? (
          <div className="actions">
            <button
              type="button"
              className="authoring-field-icon-btn"
              disabled={busy || applyBusy || props.checklist === null}
              title="Создать новую версию"
              aria-label="Создать новую версию"
              onClick={createNewVersion}
            >
              <span aria-hidden="true">＋</span>
            </button>
          </div>
        ) : null}

        {confirmRow !== null ? (
          <div
            role="dialog"
            aria-label="Подтверждение удаления черновика"
            className="banner banner-conflict"
          >
            <p>
              Удалить черновик из работы? Черновик будет закрыт для редактирования. Архивные версии и
              аудиторские записи сохраняются.
            </p>
            <div className="actions">
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setBusy(true);
                  setError(null);
                  void props.api
                    .discardTemplateAuthoringDraft(confirmRow.id, confirmRow.version)
                    .then(() => {
                      setConfirmDiscardId(null);
                      setBusy(false);
                      props.onDraftsChanged();
                    })
                    .catch((failure: unknown) => {
                      setError(failure);
                      setBusy(false);
                    });
                }}
              >
                Да, исключить черновик из работы
              </button>
              <button type="button" onClick={() => setConfirmDiscardId(null)}>
                Не удалять
              </button>
            </div>
          </div>
        ) : null}

        {applyPhase === 'assessing' ? (
          <p className="meta" role="status">
            Проверяем, какие осмотры затронуты…
          </p>
        ) : null}
        {applyPhase === 'needs-reassess' ? (
          <div className="admin-template-apply-impact" role="status">
            <p>{applyHint ?? 'Данные изменились. Оцените влияние ещё раз.'}</p>
            <div className="actions">
              <button
                type="button"
                disabled={busy || applyVersionId === null || props.checklist === null}
                onClick={() => {
                  if (props.checklist === null || applyVersionId === null) {
                    return;
                  }
                  const versionId = applyVersionId;
                  setError(null);
                  setApplyHint(null);
                  setBusy(true);
                  props.onCatalogChanged?.();
                  void props.api
                    .listChecklistTemplates()
                    .then((rows) => {
                      const fresh = rows.find((row) => row.id === props.checklistId);
                      if (fresh === undefined) {
                        throw new Error('Чек-лист отсутствует в загруженном каталоге.');
                      }
                      assessAndConfirm(versionId, fresh.version);
                    })
                    .catch((failure: unknown) => {
                      setApplyPhase('needs-reassess');
                      setBusy(false);
                      setError(failure);
                    });
                }}
              >
                Повторить оценку
              </button>
              <button
                type="button"
                onClick={() => {
                  resetApplySurface();
                  setNotice('Версия сохранена, но применить её не удалось.');
                  void refreshVersions();
                  props.onDraftsChanged();
                }}
              >
                Отмена
              </button>
            </div>
          </div>
        ) : null}
        {(applyPhase === 'confirm' || applyPhase === 'activating') && assessment !== null ? (
          <div
            className="admin-template-apply-impact"
            role="region"
            aria-label="Что изменится после применения"
          >
            <h3 className="section-title">Что изменится после применения</h3>
            <p>{TEMPLATE_APPLY_IMPACT_INTRO}</p>
            <ul className="admin-template-impact-list">
              {templateActivationImpactRows(assessment).map((row) => (
                <li
                  key={row.key}
                  className={
                    row.emphasize
                      ? 'admin-template-impact-row is-emphasized'
                      : 'admin-template-impact-row'
                  }
                >
                  <p className="admin-template-impact-title">
                    {row.title}
                    <span className="admin-template-impact-count"> · {row.count}</span>
                  </p>
                  <p className="meta">{row.explanation}</p>
                </li>
              ))}
            </ul>
            {assessment.counts.unacknowledged_devices > 0 ? (
              <p className="meta">
                Устройства без подтверждения загрузки: {assessment.counts.unacknowledged_devices}
              </p>
            ) : null}
            {applyPhase === 'activating' ? (
              <p className="meta" role="status">
                Применяем изменения…
              </p>
            ) : null}
            <div className="actions">
              <button
                type="button"
                disabled={applyPhase === 'activating' || busy}
                onClick={confirmActivate}
              >
                Применить изменения
              </button>
              <button
                type="button"
                disabled={applyPhase === 'activating'}
                onClick={() => {
                  resetApplySurface();
                  setBusy(false);
                  setNotice('Версия сохранена, но применить её не удалось.');
                  void refreshVersions();
                  props.onDraftsChanged();
                }}
              >
                Отмена
              </button>
            </div>
            <p className="meta">{TEMPLATE_APPLY_IMPACT_INTRO}</p>
          </div>
        ) : null}
      </section>

      {props.checklist !== null ? (
        <TechnicalDetails>
          <p>Код чек-листа: {props.checklist.template_code}</p>
          <p>Полное название: {props.checklist.name}</p>
          <p>Идентификатор чек-листа: {props.checklist.id}</p>
          <p>Оптимистичная версия: {props.checklist.version}</p>
        </TechnicalDetails>
      ) : null}
    </>
  );
}

function TechnicalDetails(props: { readonly children: ReactNode }): ReactElement {
  return (
    <details className="admin-technical-details">
      <summary>Технические сведения</summary>
      {props.children}
    </details>
  );
}

function CreateDraftFlow(props: {
  readonly api: AdminApi;
  readonly templates: ChecklistTemplateAdmin[] | null;
  readonly mode: 'choose' | 'manual' | 'excel' | 'clone';
  readonly onModeChange: (mode: 'choose' | 'manual' | 'excel' | 'clone') => void;
  readonly onCancel: () => void;
  readonly onCreated: (target: CreatedDraftTarget) => void;
  readonly onCatalogChanged?: () => void;
}): ReactElement {
  if (props.mode === 'choose') {
    return (
      <>
        <h2>Создать чек-лист</h2>
        <p className="meta">Все пути открывают один и тот же конструктор версий.</p>
        <div className="actions">
          <button type="button" onClick={() => props.onModeChange('manual')}>
            Создать с нуля
          </button>
          <button type="button" onClick={() => props.onModeChange('excel')}>
            Импортировать из Excel
          </button>
          <button type="button" onClick={() => props.onModeChange('clone')}>
            Создать на основе существующего
          </button>
          <button type="button" onClick={props.onCancel}>
            Отмена
          </button>
        </div>
      </>
    );
  }
  if (props.mode === 'manual') {
    return (
      <ManualCreateForm
        api={props.api}
        onBack={() => props.onModeChange('choose')}
        onCreated={props.onCreated}
        onCatalogChanged={props.onCatalogChanged}
      />
    );
  }
  if (props.mode === 'clone') {
    return (
      <CloneCreateForm
        api={props.api}
        templates={props.templates}
        onBack={() => props.onModeChange('choose')}
        onCreated={props.onCreated}
        onCatalogChanged={props.onCatalogChanged}
      />
    );
  }
  return (
    <ExcelCreateForm
      api={props.api}
      templates={props.templates}
      onBack={() => props.onModeChange('choose')}
      onCreated={props.onCreated}
    />
  );
}

function ManualCreateForm(props: {
  readonly api: AdminApi;
  readonly onBack: () => void;
  readonly onCreated: (target: CreatedDraftTarget) => void;
  readonly onCatalogChanged?: () => void;
}): ReactElement {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [partialNotice, setPartialNotice] = useState<string | null>(null);

  return (
    <>
      <h2>Создать с нуля</h2>
      <p className="meta">
        Будет создан новый чек-лист и открыт черновик первой версии. Публикация и применение версии
        выполняются отдельно.
      </p>
      {error !== null ? <ProblemAlert failure={error} /> : null}
      {partialNotice !== null ? (
        <p role="status" className="banner">
          {partialNotice}
        </p>
      ) : null}
      <label htmlFor="tad_manual_name">
        Название чек-листа
        <input
          id="tad_manual_name"
          value={name}
          disabled={busy}
          placeholder="Название нового чек-листа"
          onChange={(event) => setName(event.target.value)}
        />
      </label>
      <div className="actions">
        <button
          type="button"
          disabled={busy || name.trim().length === 0}
          onClick={() => {
            const trimmed = name.trim();
            if (trimmed.length === 0) {
              return;
            }
            setBusy(true);
            setError(null);
            setPartialNotice(null);
            const code = generateInternalCode('ChecklistTemplate');
            void props.api
              .createChecklistTemplate(code, trimmed)
              .then((template) => {
                props.onCatalogChanged?.();
                return props.api
                  .createTemplateAuthoringDraft(template.id, template.version)
                  .then((draft) => {
                    props.onCreated({
                      draftId: draft.id,
                      checklistTemplateId: template.id,
                    });
                  })
                  .catch((failure: unknown) => {
                    setPartialNotice(
                      'Чек-лист создан, но черновик не удалось создать. Его можно продолжить в разделе Конструктор.',
                    );
                    setError(failure);
                    setBusy(false);
                  });
              })
              .catch((failure: unknown) => {
                setError(failure);
                setBusy(false);
              });
          }}
        >
          Создать и открыть черновик
        </button>
        <button type="button" disabled={busy} onClick={props.onBack}>
          Назад
        </button>
      </div>
    </>
  );
}

type CloneCreateIntent = 'same' | 'new';

function CloneCreateForm(props: {
  readonly api: AdminApi;
  readonly templates: ChecklistTemplateAdmin[] | null;
  readonly onBack: () => void;
  readonly onCreated: (target: CreatedDraftTarget) => void;
  readonly onCatalogChanged?: () => void;
}): ReactElement {
  const [sourceTemplateId, setSourceTemplateId] = useState('');
  const [sourceVersionId, setSourceVersionId] = useState('');
  const [intent, setIntent] = useState<CloneCreateIntent | null>(null);
  const [newName, setNewName] = useState('');
  const [createdTarget, setCreatedTarget] = useState<{
    readonly id: string;
    readonly version: string;
    readonly name: string;
  } | null>(null);
  const [versions, setVersions] = useState<Awaited<
    ReturnType<AdminApi['listChecklistTemplateVersions']>
  > | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [partialNotice, setPartialNotice] = useState<string | null>(null);
  const loadSeq = useRef(0);

  useEffect(() => {
    if (sourceTemplateId.length === 0) {
      setVersions(null);
      setSourceVersionId('');
      setIntent(null);
      return;
    }
    const seq = ++loadSeq.current;
    void props.api
      .listChecklistTemplateVersions(sourceTemplateId)
      .then((items) => {
        if (seq !== loadSeq.current) {
          return;
        }
        setVersions(items);
        setSourceVersionId('');
        setIntent(null);
      })
      .catch((failure: unknown) => {
        if (seq !== loadSeq.current) {
          return;
        }
        setError(failure);
        setVersions(null);
      });
  }, [props.api, sourceTemplateId]);

  useEffect(() => {
    if (sourceVersionId.length > 0 && intent === null) {
      setIntent('same');
    }
  }, [sourceVersionId, intent]);

  const liveSourceTemplate =
    props.templates?.find((row) => row.id === sourceTemplateId) ?? null;
  const sourceDisplayName =
    liveSourceTemplate !== null
      ? templatePrimaryDisplayName(liveSourceTemplate.name)
      : 'чек-лист';
  const sourceVersion = versions?.find((row) => row.id === sourceVersionId) ?? null;

  const createSameVersionDraft = () => {
    if (sourceVersion === null || liveSourceTemplate === null) {
      return;
    }
    setBusy(true);
    setError(null);
    setPartialNotice(null);
    void props.api
      .createTemplateAuthoringDraftFromTemplateVersion(
        sourceVersion.id,
        liveSourceTemplate.id,
        liveSourceTemplate.version,
      )
      .then((draft) =>
        props.onCreated({
          draftId: draft.id,
          checklistTemplateId: liveSourceTemplate.id,
        }),
      )
      .catch((failure: unknown) => {
        setError(failure);
        setBusy(false);
      });
  };

  const createNewChecklistDraft = (target: {
    readonly id: string;
    readonly version: string;
  }) => {
    if (sourceVersion === null) {
      return;
    }
    setBusy(true);
    setError(null);
    void props.api
      .createTemplateAuthoringDraftFromTemplateVersion(
        sourceVersion.id,
        target.id,
        target.version,
      )
      .then((draft) =>
        props.onCreated({
          draftId: draft.id,
          checklistTemplateId: target.id,
        }),
      )
      .catch((failure: unknown) => {
        setPartialNotice(
          'Чек-лист создан, но черновик на основе выбранной версии создать не удалось.',
        );
        setError(failure);
        setBusy(false);
      });
  };

  const startOptionB = () => {
    if (sourceVersion === null) {
      return;
    }
    if (createdTarget !== null) {
      createNewChecklistDraft(createdTarget);
      return;
    }
    const trimmed = newName.trim();
    if (trimmed.length === 0) {
      return;
    }
    setBusy(true);
    setError(null);
    setPartialNotice(null);
    const code = generateInternalCode('ChecklistTemplate');
    void props.api
      .createChecklistTemplate(code, trimmed)
      .then((template) => {
        const target = {
          id: template.id,
          version: template.version,
          name: template.name,
        };
        setCreatedTarget(target);
        props.onCatalogChanged?.();
        return props.api
          .createTemplateAuthoringDraftFromTemplateVersion(
            sourceVersion.id,
            template.id,
            template.version,
          )
          .then((draft) =>
            props.onCreated({
              draftId: draft.id,
              checklistTemplateId: template.id,
            }),
          )
          .catch((failure: unknown) => {
            setPartialNotice(
              'Чек-лист создан, но черновик на основе выбранной версии создать не удалось.',
            );
            setError(failure);
            setBusy(false);
          });
      })
      .catch((failure: unknown) => {
        setError(failure);
        setBusy(false);
      });
  };

  const canSubmitSame =
    !busy && sourceVersion !== null && liveSourceTemplate !== null && intent === 'same';
  const canSubmitNew =
    !busy &&
    sourceVersion !== null &&
    intent === 'new' &&
    (createdTarget !== null || newName.trim().length > 0);

  return (
    <>
      <h2>Создать на основе существующего</h2>
      <p className="meta">
        Выберите чек-лист и версию, структуру которой хотите использовать.
      </p>
      {error !== null ? <ProblemAlert failure={error} /> : null}
      {partialNotice !== null ? (
        <p role="status" className="banner">
          {partialNotice}
        </p>
      ) : null}
      <label htmlFor="tad_clone_source_template">
        Исходный чек-лист
        <select
          id="tad_clone_source_template"
          value={sourceTemplateId}
          disabled={busy || createdTarget !== null}
          onChange={(event) => {
            setSourceTemplateId(event.target.value);
            setCreatedTarget(null);
            setPartialNotice(null);
          }}
        >
          <option value="">Выберите чек-лист</option>
          {(props.templates ?? []).map((row) => (
            <option key={row.id} value={row.id}>
              {templateBusinessLabel(row)}
            </option>
          ))}
        </select>
      </label>
      <label htmlFor="tad_clone_source_version">
        Исходная версия
        <select
          id="tad_clone_source_version"
          value={sourceVersionId}
          disabled={busy || versions === null || createdTarget !== null}
          onChange={(event) => {
            setSourceVersionId(event.target.value);
            setCreatedTarget(null);
            setPartialNotice(null);
          }}
        >
          <option value="">Выберите версию</option>
          {(versions ?? []).map((row) => (
            <option key={row.id} value={row.id}>
              {templateVersionBusinessLabel(row)}
            </option>
          ))}
        </select>
      </label>
      {sourceVersion !== null ? (
        <fieldset className="admin-clone-intent">
          <legend>Что хотите создать?</legend>
          <label className="admin-choice" htmlFor="tad_clone_intent_same">
            <input
              id="tad_clone_intent_same"
              className="admin-choice__control"
              type="radio"
              name="tad_clone_intent"
              checked={intent === 'same'}
              disabled={busy || createdTarget !== null}
              aria-label="Новую версию этого чек-листа"
              onChange={() => setIntent('same')}
            />
            <span>
              <span className="admin-clone-intent-title">Новую версию этого чек-листа</span>
              <span className="meta">
                Изменения останутся в чек-листе «{sourceDisplayName}». Его привязки к пакетам
                спецтехники сохранятся.
              </span>
            </span>
          </label>
          <label className="admin-choice" htmlFor="tad_clone_intent_new">
            <input
              id="tad_clone_intent_new"
              className="admin-choice__control"
              type="radio"
              name="tad_clone_intent"
              checked={intent === 'new'}
              disabled={busy || createdTarget !== null}
              aria-label="Новый отдельный чек-лист"
              onChange={() => setIntent('new')}
            />
            <span>
              <span className="admin-clone-intent-title">Новый отдельный чек-лист</span>
              <span className="meta">
                Будет создан отдельный чек-лист со своими версиями и привязками. Структура будет
                скопирована из выбранной версии.
              </span>
            </span>
          </label>
        </fieldset>
      ) : null}
      {intent === 'new' && createdTarget === null ? (
        <label htmlFor="tad_clone_new_name">
          Название нового чек-листа
          <input
            id="tad_clone_new_name"
            value={newName}
            disabled={busy}
            placeholder="Например, ПС2"
            onChange={(event) => setNewName(event.target.value)}
          />
        </label>
      ) : null}
      {intent === 'new' && createdTarget !== null ? (
        <p className="meta" role="status">
          Чек-лист «{templatePrimaryDisplayName(createdTarget.name)}» уже создан. Можно повторить
          создание черновика.
        </p>
      ) : null}
      <div className="actions">
        {intent === 'same' ? (
          <button type="button" disabled={!canSubmitSame} onClick={createSameVersionDraft}>
            Создать черновик новой версии
          </button>
        ) : null}
        {intent === 'new' ? (
          <button
            type="button"
            disabled={!canSubmitNew}
            onClick={() => {
              if (createdTarget !== null) {
                createNewChecklistDraft(createdTarget);
                return;
              }
              startOptionB();
            }}
          >
            {createdTarget !== null ? 'Повторить создание черновика' : 'Создать новый чек-лист'}
          </button>
        ) : null}
        <button type="button" disabled={busy} onClick={props.onBack}>
          Назад
        </button>
      </div>
    </>
  );
}

function ExcelCreateForm(props: {
  readonly api: AdminApi;
  readonly templates: ChecklistTemplateAdmin[] | null;
  readonly onBack: () => void;
  readonly onCreated: (target: CreatedDraftTarget) => void;
}): ReactElement {
  const [profiles, setProfiles] = useState<TemplateImportProfile[] | null>(null);
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [revisions, setRevisions] = useState<
    Awaited<ReturnType<AdminApi['listImportRevisions']>>['items'] | null
  >(null);
  const [selectedRevisionId, setSelectedRevisionId] = useState('');
  const [artifact, setArtifact] = useState<TemplateSourceArtifact | null>(null);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [run, setRun] = useState<TemplateImportRun | null>(null);
  const [newProfileCode, setNewProfileCode] = useState('');
  const [newProfileFamily, setNewProfileFamily] = useState('generic');
  const [mapping, setMapping] = useState<DeclarativeMapping>(defaultMappingForFamily('generic'));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const selectedProfile = profiles?.find((row) => row.id === selectedProfileId) ?? null;
  const canCreateRun =
    artifact !== null &&
    selectedSheet.length > 0 &&
    selectedRevisionId.length > 0 &&
    selectedTemplateId.length > 0 &&
    !busy;

  const reloadProfiles = () => {
    void props.api
      .listImportProfiles()
      .then(setProfiles)
      .catch((failure: unknown) => setError(failure));
  };

  useEffect(() => {
    reloadProfiles();
  }, [props.api]);

  useEffect(() => {
    if (selectedProfileId.length === 0) {
      setRevisions(null);
      setSelectedRevisionId('');
      return;
    }
    void props.api
      .listImportRevisions(selectedProfileId)
      .then((result) => {
        setRevisions(result.items);
        const current = result.items.find((row) => row.is_current) ?? result.items[0] ?? null;
        setSelectedRevisionId(current?.id ?? '');
      })
      .catch((failure: unknown) => setError(failure));
  }, [props.api, selectedProfileId]);

  const mutate = (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    void work()
      .catch((failure: unknown) => setError(failure))
      .finally(() => setBusy(false));
  };

  return (
    <>
      <h2>Импорт Excel → черновик конструктора</h2>
      <p className="meta">
        Файл загружается на сервер. После ImportRun создаётся IMPORTED-черновик и открывается общий
        редактор. Публикация через устаревший publishTemplateImportRun не используется.
      </p>
      {error !== null ? <ProblemAlert failure={error} /> : null}
      {success !== null ? (
        <p role="status" className="banner">
          {success}
        </p>
      ) : null}
      <label htmlFor="tad_excel_file">
        Файл .xlsx
        <input
          id="tad_excel_file"
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          disabled={busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file === undefined) {
              return;
            }
            mutate(async () => {
              const uploaded = await props.api.uploadTemplateSource(file);
              setArtifact(uploaded);
              setSelectedSheet(uploaded.discovered_sheets[0] ?? '');
              setRun(null);
              setSuccess(
                `Загружен ${normalizeLogicalFilename(uploaded.original_filename)}. Листов: ${uploaded.discovered_sheets.length}.`,
              );
            });
          }}
        />
      </label>
      {artifact !== null ? (
        <>
          <p className="meta">
            Файл: {normalizeLogicalFilename(artifact.original_filename)} · размер{' '}
            {artifact.byte_size} байт
          </p>
          <label htmlFor="tad_excel_sheet">
            Лист книги
            <select
              id="tad_excel_sheet"
              value={selectedSheet}
              disabled={busy}
              onChange={(event) => setSelectedSheet(event.target.value)}
            >
              <option value="">Выберите лист</option>
              {artifact.discovered_sheets.map((sheet) => (
                <option key={sheet} value={sheet}>
                  {sheet}
                </option>
              ))}
            </select>
          </label>
        </>
      ) : null}
      <label htmlFor="tad_excel_profile">
        Профиль импорта
        <select
          id="tad_excel_profile"
          value={selectedProfileId}
          disabled={busy || profiles === null}
          onChange={(event) => setSelectedProfileId(event.target.value)}
        >
          <option value="">Выберите профиль</option>
          {(profiles ?? []).map((row) => (
            <option key={row.id} value={row.id}>
              {row.code} · {row.source_family}
            </option>
          ))}
        </select>
      </label>
      <fieldset>
        <legend>Создать профиль при необходимости</legend>
        <label htmlFor="tad_excel_new_profile_code">
          Код профиля
          <input
            id="tad_excel_new_profile_code"
            value={newProfileCode}
            disabled={busy}
            onChange={(event) => setNewProfileCode(event.target.value)}
          />
        </label>
        <label htmlFor="tad_excel_new_profile_family">
          Семейство источника
          <input
            id="tad_excel_new_profile_family"
            value={newProfileFamily}
            disabled={busy}
            onChange={(event) => setNewProfileFamily(event.target.value)}
          />
        </label>
        <button
          type="button"
          disabled={busy || newProfileCode.trim().length === 0}
          onClick={() => {
            mutate(async () => {
              const created = await props.api.createImportProfile(
                newProfileCode.trim(),
                newProfileFamily.trim() || 'generic',
              );
              setNewProfileCode('');
              reloadProfiles();
              setSelectedProfileId(created.id);
              setMapping(defaultMappingForFamily(created.source_family));
              setSuccess(`Профиль ${created.code} создан.`);
            });
          }}
        >
          Создать профиль
        </button>
      </fieldset>
      <label htmlFor="tad_excel_revision">
        Версия настроек импорта
        <select
          id="tad_excel_revision"
          value={selectedRevisionId}
          disabled={busy || revisions === null}
          onChange={(event) => setSelectedRevisionId(event.target.value)}
        >
          <option value="">Выберите версию настроек</option>
          {(revisions ?? []).map((row) => (
            <option key={row.id} value={row.id}>
              Версия настроек {row.revision_no}
              {row.is_current ? ' (текущая)' : ''}
            </option>
          ))}
        </select>
      </label>
      {selectedProfile !== null && (revisions === null || revisions.length === 0) ? (
        <fieldset>
          <legend>Сохранить первую версию настроек</legend>
          <p className="meta">
            Минимальный mapping для семейства {selectedProfile.source_family}. Оценка платформы:
            {ASSESSMENTS.join(', ')}.
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              const payload = buildMappingPayload(mapping);
              const failures = validateMapping(mapping);
              if (failures.length > 0) {
                setError({
                  message: failures.map((item) => item.message).join('; ') || 'mapping invalid',
                });
                return;
              }
              mutate(async () => {
                await props.api.createImportRevision(
                  selectedProfile.id,
                  selectedProfile.version,
                  payload,
                  { schema: 'header-binding-overlay/v1', aliases: [] },
                );
                const listed = await props.api.listImportRevisions(selectedProfile.id);
                setRevisions(listed.items);
                const current = listed.items.find((row) => row.is_current) ?? listed.items[0];
                setSelectedRevisionId(current?.id ?? '');
                const refreshed = await props.api.listImportProfiles();
                setProfiles(refreshed);
                setSuccess('Версия настроек импорта сохранена.');
              });
            }}
          >
            Сохранить новую версию настроек
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setMapping(defaultMappingForFamily(selectedProfile.source_family));
              if (selectedRevisionId.length === 0) {
                return;
              }
              void props.api
                .getImportRevision(selectedProfile.id, selectedRevisionId)
                .then((revision) => {
                  const parsed = mappingFromUnknown(revision.mapping);
                  if (parsed.ok && parsed.mapping.schema === MAPPING_SCHEMA) {
                    setMapping(parsed.mapping);
                  }
                })
                .catch(() => undefined);
            }}
          >
            Сбросить mapping по умолчанию
          </button>
        </fieldset>
      ) : null}
      <label htmlFor="tad_excel_template">
        Чек-лист
        <select
          id="tad_excel_template"
          value={selectedTemplateId}
          disabled={busy || props.templates === null}
          onChange={(event) => setSelectedTemplateId(event.target.value)}
        >
          <option value="">Выберите чек-лист</option>
          {(props.templates ?? []).map((row) => (
            <option key={row.id} value={row.id}>
              {templateBusinessLabel(row)}
            </option>
          ))}
        </select>
      </label>
      <div className="actions">
        <button
          type="button"
          disabled={!canCreateRun}
          onClick={() => {
            if (artifact === null) {
              return;
            }
            mutate(async () => {
              const created = await props.api.createImportRun({
                source_artifact_id: artifact.id,
                source_sheet: selectedSheet,
                import_profile_revision_id: selectedRevisionId,
                checklist_template_id: selectedTemplateId,
              });
              setRun(created);
              setSuccess(`Импорт выполнен. Статус: ${importRunStatusLabel(created.status)}.`);
            });
          }}
        >
          Импортировать лист
        </button>
        <button type="button" disabled={busy} onClick={props.onBack}>
          Назад
        </button>
      </div>
      {run !== null ? (
        <>
          <p>
            Статус импорта: {importRunStatusLabel(run.status)}
            {run.validation_report !== null
              ? ` · ошибок ${run.validation_report.error_count} · предупреждений ${run.validation_report.warning_count}`
              : ''}
          </p>
          {run.validation_report?.issues.map((issue, index) => {
            const context = issueContext(issue);
            return (
              <div
                key={`${issue.code}-${index}`}
                role={issue.severity === 'ERROR' ? 'alert' : 'status'}
                className={issue.severity === 'ERROR' ? 'banner banner-closed' : 'banner'}
              >
                <p>
                  {issue.severity === 'ERROR' ? 'Ошибка' : 'Предупреждение'}:{' '}
                  {validationIssueExplanation(issue.code)}
                </p>
                <p className="meta">
                  {issue.code}
                  {context !== null ? ` · ${context}` : ''}
                </p>
              </div>
            );
          })}
          <button
            type="button"
            disabled={busy || !canPublishImportRun(run)}
            onClick={() => {
              mutate(async () => {
                const draft = await props.api.createTemplateAuthoringDraftFromImportRun(
                  run.id,
                  run.version,
                );
                props.onCreated({
                  draftId: draft.id,
                  checklistTemplateId: selectedTemplateId,
                });
              });
            }}
          >
            Создать черновик конструктора
          </button>
          {!canPublishImportRun(run) ? (
            <p className="meta">
              Черновик доступен, когда сервер переведёт импорт в состояние «Готов к публикации».
            </p>
          ) : null}
        </>
      ) : null}
    </>
  );
}
